import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Tab,
  Tabs,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  CheckCircleOutline,
  Close,
  Delete,
  Download,
  Edit,
  InfoOutlined,
  RestoreFromTrash,
  UploadFile,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import FileDropzone from "../components/FileDropzone";
import SavingOverlay from "../components/SavingOverlay";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import StandardPagination, {
  ITENS_POR_PAGINA_PADRAO,
} from "../components/Pagination/StandardPagination";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import {
  Institution,
  InstitutionCatalogItem,
  InstitutionCatalogKind,
  InstitutionCatalogs,
  InstitutionImportResult,
  InstitutionKind,
  institutionsService,
  mapInstitutionError,
} from "../services/institutionsService";

/**
 * Instituições da entidade (RN-553): escolas, creches, empresas, clubes, CAPS, CRAS, abrigos e
 * grupos comunitários com que os pacientes têm vínculo. Cadastro da clínica, não do paciente — a
 * mesma escola serve a vários. Os contatos do dia a dia (professora, coordenadora) ficam no
 * vínculo de cada paciente, porque mudam de aluno para aluno.
 */

const NAME_MAX = 150;

const sectionStyle = { mb: 3 };

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  "&:hover": { backgroundColor: hover },
});

/** Vazio = não informado; separado de "não é" — por isso não é boolean puro. */
type NonProfitChoice = "" | "yes" | "no";

interface FormState {
  id: string | null;
  name: string;
  address: string;
  number: string;
  neighborhood: string;
  zipCode: string;
  phone: string;
  email: string;
  director: string;
  supervisor: string;
  services: string;
  natureId: string;
  sphereId: string;
  segmentId: string;
  nonProfit: NonProfitChoice;
  kind: InstitutionKind;
  professionalTypeId: string;
  professionalTypeOther: string;
}

const emptyForm = (): FormState => ({
  id: null,
  name: "",
  address: "",
  number: "",
  neighborhood: "",
  zipCode: "",
  phone: "",
  email: "",
  director: "",
  supervisor: "",
  services: "",
  natureId: "",
  sphereId: "",
  segmentId: "",
  nonProfit: "",
  kind: "institution",
  professionalTypeId: "",
  professionalTypeOther: "",
});

const formOf = (institution: Institution): FormState => ({
  id: institution.id,
  name: institution.name,
  address: institution.address ?? "",
  number: institution.number ?? "",
  neighborhood: institution.neighborhood ?? "",
  zipCode: institution.zipCode ?? "",
  phone: institution.phone ?? "",
  email: institution.email ?? "",
  director: institution.director ?? "",
  supervisor: institution.supervisor ?? "",
  services: institution.services ?? "",
  natureId: institution.natureId ?? "",
  sphereId: institution.sphereId ?? "",
  segmentId: institution.segmentId ?? "",
  nonProfit: institution.nonProfit === null ? "" : institution.nonProfit ? "yes" : "no",
  kind: institution.kind,
  professionalTypeId: institution.professionalTypeId ?? "",
  // Categoria escrita à mão: veio sem id, mas com nome
  professionalTypeOther:
    !institution.professionalTypeId && institution.professionalTypeName
      ? institution.professionalTypeName
      : "",
});


/**
 * Aba dos três cadastros de apoio (RN-553c). Uma lista por catálogo, cada uma com incluir,
 * renomear e inativar. Inativar item em uso é recusado pelo servidor — a contagem ao lado do
 * nome avisa antes de a pessoa tentar.
 */
const CatalogList: React.FC<{
  titulo: string;
  ajuda: string;
  kind: InstitutionCatalogKind;
  itens: InstitutionCatalogItem[];
  onSave: (kind: InstitutionCatalogKind, item: InstitutionCatalogItem | null, name: string) => void;
  onToggle: (kind: InstitutionCatalogKind, item: InstitutionCatalogItem) => void;
}> = ({ titulo, ajuda, kind, itens, onSave, onToggle }) => {
  const [novo, setNovo] = useState("");
  const [editando, setEditando] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState("");

  return (
    <Box sx={{ flex: "1 1 260px", minWidth: 240 }}>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: colors.textPrimary }}>
        {titulo}
      </Typography>
      <Typography sx={{ fontSize: "0.78rem", color: colors.textSecondary, mb: 1 }}>{ajuda}</Typography>

      <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
        <TextField
          size="small"
          placeholder="Incluir item"
          value={novo}
          onChange={(e) => setNovo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && novo.trim()) {
              onSave(kind, null, novo);
              setNovo("");
            }
          }}
          inputProps={{ maxLength: 60 }}
          fullWidth
        />
        <Tooltip title="Incluir">
          <span>
            <IconButton
              onClick={() => {
                onSave(kind, null, novo);
                setNovo("");
              }}
              disabled={!novo.trim()}
              sx={{ backgroundColor: colors.primary, color: "#fff", "&:hover": { backgroundColor: colors.primaryHover } }}
            >
              <Add />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {itens.length === 0 ? (
        <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>
          Nenhum item cadastrado.
        </Typography>
      ) : (
        itens.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              py: 0.5,
              borderBottom: "1px solid #f1f3f5",
              opacity: item.active ? 1 : 0.55,
            }}
          >
            {editando === item.id ? (
              <TextField
                size="small"
                value={rascunho}
                onChange={(e) => setRascunho(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onSave(kind, item, rascunho);
                    setEditando(null);
                  }
                  if (e.key === "Escape") setEditando(null);
                }}
                inputProps={{ maxLength: 60 }}
                autoFocus
                fullWidth
              />
            ) : (
              <Typography sx={{ fontSize: "0.85rem", flex: 1, minWidth: 0 }}>
                {item.name}
                {item.useCount > 0 && (
                  <Typography component="span" sx={{ fontSize: "0.72rem", color: "#adb5bd", ml: 0.75 }}>
                    {item.useCount} em uso
                  </Typography>
                )}
              </Typography>
            )}

            <Tooltip title={editando === item.id ? "Salvar" : "Renomear"}>
              <IconButton
                size="small"
                onClick={() => {
                  if (editando === item.id) {
                    onSave(kind, item, rascunho);
                    setEditando(null);
                  } else {
                    setRascunho(item.name);
                    setEditando(item.id);
                  }
                }}
                sx={{ color: colors.primary }}
              >
                {editando === item.id ? <CheckCircleOutline sx={{ fontSize: "1rem" }} /> : <Edit sx={{ fontSize: "1rem" }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title={item.active ? "Inativar" : "Reativar"}>
              <IconButton
                size="small"
                onClick={() => onToggle(kind, item)}
                sx={{ color: item.active ? "#dc3545" : colors.success }}
              >
                {item.active ? <Delete sx={{ fontSize: "1rem" }} /> : <RestoreFromTrash sx={{ fontSize: "1rem" }} />}
              </IconButton>
            </Tooltip>
          </Box>
        ))
      )}
    </Box>
  );
};

const CatalogPanel: React.FC<{
  catalogs: InstitutionCatalogs;
  onSave: (kind: InstitutionCatalogKind, item: InstitutionCatalogItem | null, name: string) => void;
  onToggle: (kind: InstitutionCatalogKind, item: InstitutionCatalogItem) => void;
}> = ({ catalogs, onSave, onToggle }) => (
  <Box>
    {/* A dúvida real de quem abre esta aba é "como um item se liga ao outro?" (Hilton, 07/09).
        A resposta não cabe numa frase solta: os itens NÃO se ligam entre si — quem os combina é
        cada instituição. Por isso o exemplo montado, que mostra a combinação em vez de descrevê-la. */}
    <Box className="c4u-info-box">
      <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
      <Typography>
        Estas listas <strong>não se ligam entre si</strong>. Cada instituição escolhe{" "}
        <strong>uma de cada</strong> no próprio cadastro. A Esfera é a única exceção: ela só é
        pedida quando a Natureza escolhida for pública. Item em uso não pode ser inativado.
      </Typography>
    </Box>

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        px: 1.5,
        py: 1.25,
        mb: 2,
        border: "1px dashed #ced4da",
        borderRadius: "8px",
      }}
    >
      <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary, mr: 0.5 }}>
        Como fica no cadastro:
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.textPrimary }}>
        EMEF Vila Nova
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary }}>=</Typography>
      <Chip label="Pública" size="small" sx={{ backgroundColor: "#e7f0fd", color: "#1565c0", fontWeight: 600 }} />
      <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary }}>+</Typography>
      <Chip label="Municipal" size="small" sx={{ backgroundColor: "#e8f6ec", color: "#2e7d32", fontWeight: 600 }} />
      <Typography sx={{ fontSize: "0.72rem", color: "#adb5bd" }}>(só porque é pública)</Typography>
      <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary }}>+</Typography>
      <Chip label="Educação" size="small" sx={{ backgroundColor: "#fff5e0", color: "#8a6100", fontWeight: 600 }} />
    </Box>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
      <CatalogList
        titulo="Natureza"
        ajuda="Eixo próprio. A escolhida define se a Esfera será pedida."
        kind="nature"
        itens={catalogs.natures}
        onSave={onSave}
        onToggle={onToggle}
      />
      <CatalogList
        titulo="Esfera"
        ajuda="Depende da Natureza: só é pedida quando a instituição é pública."
        kind="sphere"
        itens={catalogs.spheres}
        onSave={onSave}
        onToggle={onToggle}
      />
      <CatalogList
        titulo="Segmento"
        ajuda="Eixo próprio, independente da Natureza. Área de atuação."
        kind="segment"
        itens={catalogs.segments}
        onSave={onSave}
        onToggle={onToggle}
      />
    </Box>
  </Box>
);

const Institutions: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();
  const entityId = user?.entityId || "";

  const [items, setItems] = useState<Institution[]>([]);
  const [catalogs, setCatalogs] = useState<InstitutionCatalogs>({
    natures: [],
    spheres: [],
    segments: [],
    professionalTypes: [],
  });
  // Aba: a lista e os cadastros de apoio moram na MESMA página (pedido do Hilton)
  const [tab, setTab] = useState<"lista" | "catalogos">("lista");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"ativos" | "inativos" | "todos">("ativos");
  const [kindFilter, setKindFilter] = useState<"" | InstitutionKind>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);

  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [toDeactivate, setToDeactivate] = useState<Institution | null>(null);

  // Importador (RN-553b): modelo para baixar + área de arrastar + relatório do que entrou
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState<InstitutionImportResult | null>(null);

  const load = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const [lista, cat] = await Promise.all([
        institutionsService.getByEntity(entityId, true),
        institutionsService.catalogs(entityId),
      ]);
      setItems(lista);
      setCatalogs(cat);
    } catch {
      showToast("Não foi possível carregar as instituições.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Instituições";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items
      .filter((i) => (status === "todos" ? true : status === "ativos" ? i.active : !i.active))
      .filter((i) => !kindFilter || i.kind === kindFilter)
      .filter((i) => !term || i.name.toLowerCase().includes(term));
  }, [items, search, status, kindFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  const pagedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasFilterChanges = !!search.trim() || status !== "ativos" || !!kindFilter;
  const clearFilters = () => {
    setSearch("");
    setStatus("ativos");
    setKindFilter("");
    setCurrentPage(1);
  };

  // Profissional externo mora na mesma tabela (RN-553g): muda o que a tela pede
  const isProfessional = form?.kind === "professional";

  // A natureza escolhida é pública? É ela que liga o campo Esfera (RN-553c)
  const publicNature = !!form?.natureId
    && catalogs.natures.some((n) => n.id === form.natureId && n.isPublic);

  const save = async () => {
    if (!form) return;
    if (!form.name.trim()) {
      showToast("Informe o nome da instituição.", "error");
      return;
    }
    if (!form.natureId) {
      showToast("Informe a natureza da instituição.", "error");
      return;
    }
    if (!form.segmentId) {
      showToast("Informe o segmento da instituição.", "error");
      return;
    }
    if (publicNature && !form.sphereId) {
      showToast("Instituição pública precisa da esfera.", "error");
      return;
    }
    if (isProfessional && !form.professionalTypeId && !form.professionalTypeOther.trim()) {
      showToast("Informe a categoria do profissional.", "error");
      return;
    }

    setSaving(true);
    try {
      await institutionsService.save({
        id: form.id,
        entityId,
        natureId: form.natureId || null,
        // Esfera só viaja quando a natureza é pública — o servidor recusa fora disso
        sphereId: publicNature ? form.sphereId || null : null,
        segmentId: form.segmentId || null,
        nonProfit: form.nonProfit === "" ? null : form.nonProfit === "yes",
        kind: form.kind,
        professionalTypeId: isProfessional ? form.professionalTypeId || null : null,
        professionalTypeOther:
          isProfessional && !form.professionalTypeId ? form.professionalTypeOther.trim() || null : null,
        name: form.name.trim(),
        address: form.address.trim() || null,
        number: form.number.trim() || null,
        neighborhood: form.neighborhood.trim() || null,
        zipCode: form.zipCode.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        director: form.director.trim() || null,
        supervisor: form.supervisor.trim() || null,
        services: form.services.trim() || null,
      });
      showToast(form.id ? "Instituição atualizada." : "Instituição incluída.", "success");
      setForm(null);
      await load();
    } catch (e: any) {
      showToast(mapInstitutionError(e), "error");
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (institution: Institution, active: boolean) => {
    setSaving(true);
    try {
      await institutionsService.setActive(institution.id, active);
      showToast(active ? "Instituição reativada." : "Instituição inativada.", "success");
      setToDeactivate(null);
      await load();
    } catch (e: any) {
      showToast(mapInstitutionError(e), "error");
      setToDeactivate(null);
    } finally {
      setSaving(false);
    }
  };

  const saveCatalogItem = async (
    kind: InstitutionCatalogKind,
    item: InstitutionCatalogItem | null,
    name: string
  ) => {
    const texto = name.trim();
    if (!texto) return;
    try {
      await institutionsService.saveCatalog(kind, {
        id: item?.id ?? null,
        entityId,
        name: texto,
        order: item?.order ?? 90,
      });
      showToast(item ? "Item atualizado." : "Item incluído.", "success");
      await load();
    } catch (e: any) {
      showToast(mapInstitutionError(e), "error");
    }
  };

  const toggleCatalogItem = async (kind: InstitutionCatalogKind, item: InstitutionCatalogItem) => {
    try {
      await institutionsService.setCatalogActive(kind, item.id, !item.active);
      await load();
    } catch (e: any) {
      showToast(mapInstitutionError(e), "error");
    }
  };

  const openImport = () => {
    setImportFile(null);
    setImportResult(null);
    setImportError("");
    setUpdateExisting(false);
    setImportOpen(true);
  };

  const closeImport = () => {
    if (importing) return;
    setImportOpen(false);
    // A lista já foi recarregada ao importar; aqui só limpamos o modal
    setImportFile(null);
    setImportResult(null);
    setImportError("");
  };

  const downloadTemplate = async () => {
    try {
      await institutionsService.downloadTemplate();
    } catch {
      showToast("Não foi possível baixar o modelo.", "error");
    }
  };

  const runImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportError("");
    try {
      const result = await institutionsService.import(entityId, importFile, updateExisting);
      setImportResult(result);
      await load();
      const total = result.created + result.updated;
      showToast(
        total > 0 ? `${total} instituição(ões) importada(s).` : "Nenhuma linha foi importada.",
        total > 0 ? "success" : "error"
      );
    } catch (e: any) {
      setImportError(mapInstitutionError(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Instituição/Profissional
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Escolas, empresas, clubes e profissionais externos com que os pacientes têm vínculo.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            {/* Os cadastros de apoio vivem na MESMA página (pedido do Hilton): trocar de tela
                para cadastrar um segmento e voltar seria perder o fio do que se estava fazendo. */}
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ mb: 2, borderBottom: "1px solid #e9ecef", minHeight: 40 }}
            >
              <Tab value="lista" label="Cadastros" sx={{ textTransform: "none", minHeight: 40 }} />
              <Tab
                value="catalogos"
                label="Natureza, esfera e segmento"
                sx={{ textTransform: "none", minHeight: 40 }}
              />
            </Tabs>

            {tab === "catalogos" && (
              <CatalogPanel
                catalogs={catalogs}
                onSave={saveCatalogItem}
                onToggle={toggleCatalogItem}
              />
            )}

            {tab === "lista" && (
            <>
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca por nome"
                  placeholder="Nome da instituição"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  fullWidth
                  sx={searchFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: NAME_MAX }}
                />
                <TextField
                  select
                  size="small"
                  label="Tipo"
                  value={kindFilter}
                  onChange={(e) => {
                    setKindFilter(e.target.value as "" | InstitutionKind);
                    setCurrentPage(1);
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="institution">Instituições</MenuItem>
                  <MenuItem value="professional">Profissionais</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as "ativos" | "inativos" | "todos");
                    setCurrentPage(1);
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="ativos">Ativas</MenuItem>
                  <MenuItem value="inativos">Inativas</MenuItem>
                  <MenuItem value="todos">Todas</MenuItem>
                </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <Tooltip title="Importar planilha de instituições">
                  <IconButton
                    onClick={openImport}
                    className="c4u-btn-icon"
                    sx={{ backgroundColor: colors.primary, color: "#fff", "&:hover": { backgroundColor: colors.primaryHover } }}
                  >
                    <UploadFile />
                  </IconButton>
                </Tooltip>
                <AddButton onClick={() => setForm(emptyForm())} title="Incluir instituição" />
              </div>
            </div>

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            )}

            {!loading && (
              <>
                <Box className="c4u-info-box">
                  <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                  <Typography>
                    Cadastre a instituição ou o profissional externo uma vez e vale para todos os
                    pacientes. O contato de quem atende cada paciente (professora, coordenadora)
                    fica na aba Vínculos dele.
                  </Typography>
                </Box>

                <Box sx={sectionStyle}>
                  <ResultCount shown={filteredItems.length} total={items.length} />

                  {filteredItems.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                      {items.length === 0
                        ? "Nenhum cadastro ainda — inclua o primeiro no botão acima."
                        : "Nenhum cadastro encontrado com os filtros aplicados."}
                    </Typography>
                  ) : (
                    <Box className="admin-plans-table">
                      <Box className="c4u-table__header">
                        <Box className="admin-plans-header-cell" sx={{ flex: "2 1 260px", minWidth: 200 }}>
                          Instituição/Profissional
                        </Box>
                        <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 200px", minWidth: 150 }}>
                          Endereço
                        </Box>
                        <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 140px" }}>
                          Telefone
                        </Box>
                        <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 200px", minWidth: 150 }}>
                          Classificação
                        </Box>
                        <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 100px" }}>
                          Vínculos
                        </Box>
                        <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px" }}>
                          Status
                        </Box>
                        <Box className="admin-plans-header-cell no-print" sx={{ flex: "0 0 120px", justifyContent: "flex-end" }}>
                          Ações
                        </Box>
                      </Box>

                      <Box className="admin-plans-table-body">
                        {pagedItems.map((item) => (
                          <Box key={item.id} className="c4u-table__row">
                            <Box
                              className="admin-plans-cell admin-plans-name"
                              sx={{ flex: "2 1 260px", minWidth: 200, gap: 0.5, flexWrap: "wrap" }}
                              data-label="Instituição/Profissional"
                            >
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{item.name}</Typography>
                              {item.kind === "professional" && (
                                <Chip
                                  label={item.professionalTypeName || "Profissional"}
                                  size="small"
                                  sx={{ height: 18, fontSize: "0.68rem", fontWeight: 600, backgroundColor: "#e7f0fd", color: "#1565c0" }}
                                />
                              )}
                              {item.services && (
                                <Typography sx={{ fontSize: 11, color: "#adb5bd", width: "100%" }}>
                                  {item.services}
                                </Typography>
                              )}
                            </Box>
                            <Box
                              className="admin-plans-cell c4u-col--lg"
                              sx={{ flex: "1 1 200px", minWidth: 150 }}
                              data-label="Endereço"
                            >
                              <Typography sx={{ fontSize: 13 }}>
                                {[item.address, item.number, item.neighborhood]
                                  .filter(Boolean)
                                  .join(", ") || "—"}
                              </Typography>
                            </Box>
                            <Box
                              className="admin-plans-cell c4u-col--md"
                              sx={{ flex: "0 0 140px" }}
                              data-label="Telefone"
                            >
                              <Typography sx={{ fontSize: 13 }}>{item.phone || "—"}</Typography>
                            </Box>
                            <Box
                              className="admin-plans-cell c4u-col--lg"
                              sx={{ flex: "1 1 200px", minWidth: 150, gap: 0.5, flexWrap: "wrap" }}
                              data-label="Classificação"
                            >
                              <Typography sx={{ fontSize: 13 }}>
                                {[item.natureName, item.sphereName, item.segmentName]
                                  .filter(Boolean)
                                  .join(" · ") || "—"}
                              </Typography>
                              {item.nonProfit === true && (
                                <Typography sx={{ fontSize: 11, color: "#adb5bd", width: "100%" }}>
                                  Sem fins lucrativos
                                </Typography>
                              )}
                            </Box>
                            <Box
                              className="admin-plans-cell c4u-col--md"
                              sx={{ flex: "0 0 100px" }}
                              data-label="Vínculos"
                            >
                              <Typography sx={{ fontSize: 13 }}>{item.linkCount}</Typography>
                            </Box>
                            <Box
                              className="admin-plans-cell"
                              sx={{ flex: "0 0 100px" }}
                              data-label="Status"
                            >
                              <Chip
                                label={item.active ? "Ativa" : "Inativa"}
                                size="small"
                                sx={{
                                  color: "white",
                                  fontWeight: 600,
                                  backgroundColor: item.active ? colors.success : "#adb5bd",
                                }}
                              />
                            </Box>
                            <Box
                              className="admin-plans-cell admin-plans-actions no-print"
                              sx={{ flex: "0 0 120px" }}
                              data-label="Ações"
                            >
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
                                <Tooltip title="Editar instituição">
                                  <IconButton
                                    className="c4u-row-action"
                                    size="small"
                                    onClick={() => setForm(formOf(item))}
                                    sx={rowActionSx(colors.primary, colors.primaryHover)}
                                  >
                                    <Edit sx={{ fontSize: "1rem" }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={item.active ? "Inativar instituição" : "Reativar instituição"}>
                                  <IconButton
                                    className="c4u-row-action"
                                    size="small"
                                    onClick={() =>
                                      item.active ? setToDeactivate(item) : setActive(item, true)
                                    }
                                    sx={rowActionSx(
                                      item.active ? "#dc3545" : colors.success,
                                      item.active ? "#c82333" : "#218838"
                                    )}
                                  >
                                    {item.active ? (
                                      <Delete sx={{ fontSize: "1rem" }} />
                                    ) : (
                                      <RestoreFromTrash sx={{ fontSize: "1rem" }} />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {filteredItems.length > 0 && (
                    <StandardPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredItems.length}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={(n) => {
                        setItemsPerPage(n);
                        setCurrentPage(1);
                      }}
                    />
                  )}
                </Box>
              </>
            )}
            </>
            )}
          </div>
        </Container>
      </Box>

      <Dialog
        open={form !== null}
        onClose={() => !saving && setForm(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
      >
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1rem 1.25rem",
          }}
        >
          <Typography
            component="h3"
            sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}
          >
            {form?.id
              ? isProfessional
                ? "Editar profissional"
                : "Editar instituição"
              : isProfessional
                ? "Novo profissional"
                : "Nova instituição"}
          </Typography>
          <IconButton onClick={() => setForm(null)} sx={{ color: colors.white, padding: "0.25rem" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {form && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 2,
              }}
            >
              {/* O tipo vem primeiro: é ele que decide o resto do formulário. Só na inclusão —
                  mudar o tipo de um cadastro existente deixaria campos órfãos. */}
              {!form.id && (
                <TextField
                  select
                  label="Tipo de cadastro"
                  value={form.kind}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      kind: e.target.value as InstitutionKind,
                      professionalTypeId: "",
                      professionalTypeOther: "",
                    })
                  }
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
                >
                  <MenuItem value="institution">Instituição (escola, empresa, clube, CAPS...)</MenuItem>
                  <MenuItem value="professional">Profissional externo (pessoa)</MenuItem>
                </TextField>
              )}

              {isProfessional && (
                <>
                  <TextField
                    select
                    label="Categoria"
                    value={form.professionalTypeId}
                    onChange={(e) =>
                      setForm({ ...form, professionalTypeId: e.target.value, professionalTypeOther: "" })
                    }
                    helperText="A lista é das categorias da clínica"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="">
                      <em>Outra — descrever</em>
                    </MenuItem>
                    {catalogs.professionalTypes.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Neuropediatra e psiquiatra atendem fora e nunca estarão na lista da clínica */}
                  {!form.professionalTypeId && (
                    <TextField
                      label="Qual categoria"
                      value={form.professionalTypeOther}
                      onChange={(e) => setForm({ ...form, professionalTypeOther: e.target.value })}
                      placeholder="Ex.: Neuropediatra, Psiquiatra"
                      helperText="Fica só neste cadastro — não entra na lista da clínica"
                      required
                      size="small"
                      inputProps={{ maxLength: 80 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                </>
              )}

              <TextField
                label="Nome"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                size="small"
                inputProps={{ maxLength: NAME_MAX }}
                InputLabelProps={{ shrink: true }}
                placeholder={
                  isProfessional
                    ? "Ex.: Dra. Marina Alves"
                    : "Ex.: EMEF Vila Nova, CAPS Infantil Centro"
                }
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
              />
              <TextField
                label="Endereço"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                size="small"
                inputProps={{ maxLength: 200 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Número"
                value={form.number}
                onChange={(e) => setForm({ ...form, number: e.target.value })}
                size="small"
                inputProps={{ maxLength: 20 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Bairro"
                value={form.neighborhood}
                onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
                size="small"
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="CEP"
                value={form.zipCode}
                onChange={(e) => setForm({ ...form, zipCode: e.target.value })}
                size="small"
                inputProps={{ maxLength: 9 }}
                InputLabelProps={{ shrink: true }}
                placeholder="00000-000"
              />
              <TextField
                label="Telefone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                size="small"
                inputProps={{ maxLength: 120 }}
                helperText="Vários números separados por barra"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="E-mail"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                size="small"
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Diretor(a)"
                value={form.director}
                onChange={(e) => setForm({ ...form, director: e.target.value })}
                size="small"
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Supervisor(a)"
                value={form.supervisor}
                onChange={(e) => setForm({ ...form, supervisor: e.target.value })}
                size="small"
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                label="Natureza"
                value={form.natureId}
                onChange={(e) => setForm({ ...form, natureId: e.target.value, sphereId: "" })}
                required
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {catalogs.natures
                  .filter((n) => n.active || n.id === form.natureId)
                  .map((n) => (
                    <MenuItem key={n.id} value={n.id}>
                      {n.name}
                    </MenuItem>
                  ))}
              </TextField>

              {/* Esfera só existe em instituição pública: fora disso o campo nem aparece,
                  em vez de aparecer desabilitado pedindo para ser ignorado. */}
              {publicNature && (
                <TextField
                  select
                  label="Esfera"
                  value={form.sphereId}
                  onChange={(e) => setForm({ ...form, sphereId: e.target.value })}
                  required
                  size="small"
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  {catalogs.spheres
                    .filter((sp) => sp.active || sp.id === form.sphereId)
                    .map((sp) => (
                      <MenuItem key={sp.id} value={sp.id}>
                        {sp.name}
                      </MenuItem>
                    ))}
                </TextField>
              )}

              <TextField
                select
                label="Segmento"
                value={form.segmentId}
                onChange={(e) => setForm({ ...form, segmentId: e.target.value })}
                required
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {catalogs.segments
                  .filter((sg) => sg.active || sg.id === form.segmentId)
                  .map((sg) => (
                    <MenuItem key={sg.id} value={sg.id}>
                      {sg.name}
                    </MenuItem>
                  ))}
              </TextField>

              <TextField
                select
                label="Sem fins lucrativos"
                value={form.nonProfit}
                onChange={(e) => setForm({ ...form, nonProfit: e.target.value as NonProfitChoice })}
                helperText="Em branco = não informado"
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>Não informado</em>
                </MenuItem>
                <MenuItem value="yes">Sim</MenuItem>
                <MenuItem value="no">Não</MenuItem>
              </TextField>

              <TextField
                label="Atendimento / Serviços"
                value={form.services}
                onChange={(e) => setForm({ ...form, services: e.target.value })}
                multiline
                minRows={2}
                size="small"
                inputProps={{ maxLength: 500 }}
                InputLabelProps={{ shrink: true }}
                placeholder="O que a instituição oferece, turnos, observações gerais"
                sx={{
                  gridColumn: { xs: "auto", sm: "1 / -1" },
                  "& textarea": { minHeight: "0 !important", border: "none !important", padding: "0 !important" },
                }}
              />
              <Typography
                sx={{ fontSize: "0.8rem", color: colors.textSecondary, gridColumn: { xs: "auto", sm: "1 / -1" } }}
              >
                O contato de quem atende cada paciente é registrado na aba Vínculos dele — aqui
                ficam só os dados gerais da instituição.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button
            onClick={() => setForm(null)}
            variant="outlined"
            disabled={saving}
            sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={save}
            variant="contained"
            disabled={saving || !form?.name.trim()}
            sx={{ textTransform: "none", backgroundColor: colors.primary }}
          >
            {saving ? "Salvando..." : form?.id ? "Salvar" : "Incluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Importador (RN-553b): baixar modelo, arrastar o arquivo, ver o que entrou */}
      <Dialog
        open={importOpen}
        onClose={closeImport}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
      >
        <SavingOverlay open={importing} message="Importando planilha..." />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1rem 1.25rem",
          }}
        >
          <Typography
            component="h3"
            sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}
          >
            Importar instituições
          </Typography>
          <IconButton onClick={closeImport} sx={{ color: colors.white, padding: "0.25rem" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* O modelo vem ANTES da área de soltar: sem ele a pessoa não sabe o layout */}
            <Box className="c4u-info-box">
              <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
              <Typography>
                Baixe o modelo, preencha e traga de volta. Só a coluna <strong>Nome</strong> é
                obrigatória; as demais podem ficar em branco.
              </Typography>
            </Box>

            <Button
              onClick={downloadTemplate}
              variant="outlined"
              startIcon={<Download />}
              sx={{ textTransform: "none", alignSelf: "flex-start", borderColor: colors.primary, color: colors.primary }}
            >
              Baixar modelo (.xlsx)
            </Button>

            <FileDropzone
              onFile={(file) => {
                setImportFile(file);
                setImportResult(null);
                setImportError("");
              }}
              accept=".xlsx,.csv"
              disabled={importing}
              hint="Planilha .xlsx ou .csv no layout do modelo, até 500 linhas"
            />

            {importFile && (
              <Typography sx={{ fontSize: "0.85rem", color: colors.textPrimary }}>
                Arquivo escolhido: <strong>{importFile.name}</strong>
              </Typography>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                  disabled={importing}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.85rem" }}>
                  Atualizar os dados de quem já está cadastrado (pelo nome)
                </Typography>
              }
            />

            {importError && (
              <Box
                sx={{
                  backgroundColor: "#fde2e4",
                  color: colors.error,
                  borderRadius: "8px",
                  padding: "0.6rem 1rem",
                  fontSize: "0.85rem",
                }}
              >
                {importError}
              </Box>
            )}

            {importResult && (
              <Box sx={{ border: "1px solid #e9ecef", borderRadius: "8px", padding: "0.75rem 1rem" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                  <CheckCircleOutline sx={{ fontSize: 18, color: colors.success }} />
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                    {importResult.created} incluída(s), {importResult.updated} atualizada(s),{" "}
                    {importResult.skipped} fora
                  </Typography>
                </Box>
                {importResult.issues.length > 0 && (
                  <Box sx={{ maxHeight: 180, overflowY: "auto" }}>
                    {importResult.issues.map((issue) => (
                      <Typography
                        key={`${issue.row}-${issue.name}`}
                        sx={{ fontSize: "0.78rem", color: colors.textSecondary, lineHeight: 1.5 }}
                      >
                        Linha {issue.row} — {issue.name}: {issue.reason}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button
            onClick={closeImport}
            variant="outlined"
            disabled={importing}
            sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}
          >
            {importResult ? "Fechar" : "Cancelar"}
          </Button>
          <Button
            onClick={runImport}
            variant="contained"
            disabled={importing || !importFile}
            sx={{ textTransform: "none", backgroundColor: colors.primary }}
          >
            {importing ? "Importando..." : "Importar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={toDeactivate !== null}
        title="Inativar instituição"
        message={
          toDeactivate
            ? `"${toDeactivate.name}" deixa de aparecer na hora de registrar um vínculo. Os vínculos já registrados continuam mostrando o nome dela.`
            : ""
        }
        warningMessage={
          toDeactivate && toDeactivate.linkCount > 0
            ? `Há ${toDeactivate.linkCount} vínculo(s) apontando para esta instituição. Se algum estiver em curso, encerre-o antes.`
            : undefined
        }
        type="warning"
        confirmButtonText="Inativar"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={() => toDeactivate && setActive(toDeactivate, false)}
        onCancel={() => !saving && setToDeactivate(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default Institutions;
